import React from 'react';
import { withTranslation } from 'react-i18next';
import { compose, mapProps } from 'recompose';
import { graphql } from 'react-apollo';

import Button from 'components/common/Buttons/Button';
import InputField from 'components/common/InputField/InputField';
import { createPasswordValidationFn, createTextValidationFn } from 'utils/validations/validations';
import { withRouter } from 'react-router-dom';
import ThreeppModal from 'components/common/Modal/Modal';
import { getCurrentUser, updateUserProfile, setCurrentUser } from 'graphql/operations';
import { getCurrentUser as getCurrentUserSelector } from 'selectors/user/selector';

import { showSuccessMessage, showErrorMessage } from 'components/common/Alert/Alert';
import { isEmptyString } from 'utils/functions';

import './UserProfile.scss';

const INITIAL_STATE = {
  firstName: '',
  firstNameError: '',
  lastName: '',
  lastNameError: '',
  updateError: false
};

function getValidationFunction() {
  return createTextValidationFn();
}

export class UserProfile extends React.Component {
  static defaultProps = {
    toggle: k => k
  };

  constructor(props) {
    super(props);
    this.state = { ...INITIAL_STATE };
    this.validatePassword = createPasswordValidationFn();
  }

  onOpened = () => {
    const { currentUser } = this.props;
    this.setState({
      ...INITIAL_STATE,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName
    });
  };

  close = () => {
    const { toggle } = this.props;
    toggle();
  };

  onSave = async () => {
    const { currentUser, updateUserProfile: propsUpdateUserProfile, setCurrentUser: propsSetCurrentUser, t } = this.props;
    const { firstName, lastName } = this.state;
    let error = false;

    if (this.isUserNameUpdated()) {
      try {
        const {
          data: { updateUserProfile: updatedUser }
        } = await propsUpdateUserProfile({
          variables: {
            email: currentUser.email,
            firstName,
            lastName,
            country: 'US',
            position: currentUser.position
          }
        });

        await propsSetCurrentUser({
          variables: {
            email: updatedUser.email,
            firstName: updatedUser.firstName,
            lastName: updatedUser.lastName,
            position: updatedUser.position
          }
        });

        showSuccessMessage(t('userProfile.usernameUpdateSuccess'));
      } catch {
        showErrorMessage(t('userProfile.usernameUpdateError'));
        error = true;
      }
    }

    if (!error) {
      this.close();
    } else {
      this.setState({ updateError: true });
    }
  };

  setFirstName = value => {
    this.setState({
      firstName: value.trim(),
      firstNameError: '',
      updateError: false
    });
  };

  setLastName = value => {
    this.setState({
      lastName: value.trim(),
      lastNameError: '',
      updateError: false
    });
  };

  changePasswordLink = () => {
    this.close();
    let issuerUrl;
    Object.entries(localStorage).some(([key, val]) => {
      const keyPieces = key.split('.');
      if (keyPieces[0] === 'CognitoIdentityServiceProvider' && keyPieces[keyPieces.length - 1] === 'idToken') {
        issuerUrl = JSON.parse(atob(val.split('.')[1])).okta_issuer_url;
        return true;
      }
      return false;
    });
    window.open(`${issuerUrl}/enduser/settings`, '_blank');
  };

  handleFirstNameError = () => {
    const { t } = this.props;
    this.setState({
      firstNameError: t('userProfile.firstNameEmpty'),
      firstName: ''
    });
  };

  handleLastNameError = () => {
    const { t } = this.props;
    this.setState({
      lastNameError: t('userProfile.lastNameEmpty'),
      lastName: ''
    });
  };

  isUserNameUpdated() {
    const { currentUser } = this.props;
    const { firstName, lastName } = this.state;
    return !(firstName === currentUser.firstName && lastName === currentUser.lastName);
  }

  isSaveDisabled() {
    const { firstName, lastName, updateError } = this.state;
    return !this.isUserNameUpdated() || isEmptyString(firstName) || isEmptyString(lastName) || updateError;
  }

  render() {
    const { t, isOpen, toggle } = this.props;
    const { firstName, firstNameError, lastName, lastNameError } = this.state;

    const inputValidation = getValidationFunction();

    return (
      <>
        <ThreeppModal
          onOpened={this.onOpened}
          isOpen={isOpen}
          toggle={toggle}
          classes="user-profile-modal"
          header={t('userProfile.header')}
          size="sm"
        >
          <InputField
            label={t('userProfile.firstName')}
            fullWidth
            disabled={false}
            value={firstName}
            inputValidation={inputValidation}
            onChange={this.setFirstName}
            error={firstNameError}
            onValidationError={this.handleFirstNameError}
          />

          <InputField
            label={t('userProfile.lastName')}
            fullWidth
            disabled={false}
            value={lastName}
            inputValidation={inputValidation}
            onChange={this.setLastName}
            error={lastNameError}
            onValidationError={this.handleLastNameError}
          />

          {/* TODO: Fix this... */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events  */}
          <div className="change-password" onClick={this.changePasswordLink}>
            {t('userProfile.changePassword')}
          </div>

          <div className="action-row">
            {/* TODO: Fix this... */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events  */}
            <div className="btn-group">
              <Button className="btn-cancel" buttonStyle="destructive" onClick={this.close}>
                {t('common.cancel')}
              </Button>

              <Button className="btn-save" buttonStyle="primary" onClick={this.onSave} disabled={this.isSaveDisabled()}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        </ThreeppModal>
      </>
    );
  }
}

export const UserProfileHOC = compose(
  withTranslation(),
  withRouter,
  graphql(updateUserProfile, { name: 'updateUserProfile' }),
  graphql(setCurrentUser, { name: 'setCurrentUser' }),
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  mapProps(props => ({
    t: props.t,
    history: props.history,
    toggle: props.toggle,
    isOpen: props.isOpen,
    currentUser: getCurrentUserSelector(props),
    updateUserProfile: props.updateUserProfile,
    setCurrentUser: props.setCurrentUser
  }))
);

export default UserProfileHOC(UserProfile);
